/**
 * Find Tutors Page
 * Main page for browsing and searching tutors
 */

import React, { useState, useEffect } from 'react';
import { 
  Search, 
  SlidersHorizontal, 
  X, 
  GraduationCap, 
  Users,
  BookOpen,
  Trophy,
  Filter
} from 'lucide-react';
import { useAuth } from '../../../core/contexts/AuthContext';
import useTutors from '../hooks/useTutors';
import TutorCard from '../components/TutorCard';
import TutorModal from '../components/TutorModal';

const FindTutors = () => {
  const { userProfile } = useAuth();
  const {
    tutors,
    loading,
    universities,
    courseCodes,
    searchResults,
    searching,
    hasMore,
    loadTutors,
    loadMoreTutors,
    searchTutorsByCourse,
    clearSearch,
    getTutorById
  } = useTutors();

  const [showFilters, setShowFilters] = useState(false);
  const [selectedTutor, setSelectedTutor] = useState(null);
  const [showTutorModal, setShowTutorModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    university: '',
    courseCode: ''
  });
  const [appliedFilters, setAppliedFilters] = useState({
    university: '',
    courseCode: ''
  });

  const userUniversity = userProfile?.academic?.university || '';

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      searchTutorsByCourse(searchTerm.trim());
    } else {
      clearSearch();
    }
  };

  // Handle filter application
  const applyFilters = () => {
    setAppliedFilters(filters);
    loadTutors(filters, true);
    setShowFilters(false);
  };

  // Reset filters
  const resetFilters = () => {
    setFilters({ university: '', courseCode: '' });
    setAppliedFilters({ university: '', courseCode: '' });
    loadTutors({}, true);
    setShowFilters(false);
  };

  // Handle tutor card click
  const handleTutorClick = async (tutor) => {
    setSelectedTutor(tutor);
    setShowTutorModal(true);
  };

  // Close tutor modal
  const closeTutorModal = () => {
    setShowTutorModal(false);
    setSelectedTutor(null);
  };

  // Load more tutors
  const handleLoadMore = () => {
    loadMoreTutors(appliedFilters);
  };

  // Determine which tutors to display
  const displayTutors = searchResults.length > 0 ? searchResults : tutors;
  const isSearching = searchResults.length > 0;
  const hasActiveFilters = appliedFilters.university || appliedFilters.courseCode;

  return (
    <div className="container mx-auto px-4 py-6 max-w-7xl">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-2 flex items-center">
          <GraduationCap className="mr-2 text-green-500" />
          Find Tutors
        </h1>
        <p className="text-gray-600">
          Connect with verified tutors who can help you excel in your courses.
        </p>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        {/* Search Bar */}
        <form onSubmit={handleSearch} className="flex gap-3 mb-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search by course code (e.g., CSC1016S, MAM1004F)"
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <button
            type="submit"
            className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition"
            disabled={searching}
          >
            {searching ? 'Searching...' : 'Search'}
          </button>
          
          {searchTerm && (
            <button
              type="button"
              onClick={() => {
                setSearchTerm('');
                clearSearch();
              }}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition"
            >
              Clear
            </button>
          )}
        </form>

        {/* Filter Controls */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center px-3 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition"
            >
              <SlidersHorizontal size={16} className="mr-2" />
              Filters
            </button>

            {hasActiveFilters && (
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">Active filters:</span>
                {appliedFilters.university && (
                  <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                    {appliedFilters.university}
                  </span>
                )}
                {appliedFilters.courseCode && (
                  <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                    {appliedFilters.courseCode}
                  </span>
                )}
                <button
                  onClick={resetFilters}
                  className="text-xs text-red-600 hover:text-red-700"
                >
                  Clear all
                </button>
              </div>
            )}
          </div>

          <div className="text-sm text-gray-600">
            {isSearching ? (
              `${searchResults.length} results for "${searchTerm}"`
            ) : (
              `${tutors.length} tutors available`
            )}
          </div>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="mt-4 p-4 border-t border-gray-200 bg-gray-50 rounded-lg">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-sm font-medium text-gray-800">Filter Tutors</h3>
              <button
                onClick={() => setShowFilters(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={18} />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              {/* University Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  University
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  value={filters.university}
                  onChange={(e) => setFilters({ ...filters, university: e.target.value })}
                >
                  <option value="">All Universities</option>
                  {userUniversity && (
                    <option value={userUniversity}>
                      {userUniversity} (Your University)
                    </option>
                  )}
                  {universities
                    .filter(uni => uni !== userUniversity)
                    .map((university, index) => (
                      <option key={index} value={university}>
                        {university}
                      </option>
                    ))}
                </select>
              </div>

              {/* Course Code Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Course Code
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  value={filters.courseCode}
                  onChange={(e) => setFilters({ ...filters, courseCode: e.target.value })}
                >
                  <option value="">All Courses</option>
                  {courseCodes.map((code, index) => (
                    <option key={index} value={code}>
                      {code}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex justify-end space-x-2">
              <button
                onClick={resetFilters}
                className="px-3 py-1.5 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50 transition"
              >
                Reset
              </button>
              <button
                onClick={applyFilters}
                className="px-3 py-1.5 bg-green-500 text-white text-sm rounded-md hover:bg-green-600 transition"
              >
                Apply Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-3">
              <Users className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Tutors</p>
              <p className="text-xl font-semibold text-gray-800">{tutors.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
              <BookOpen className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Course Subjects</p>
              <p className="text-xl font-semibold text-gray-800">{courseCodes.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
              <Trophy className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Universities</p>
              <p className="text-xl font-semibold text-gray-800">{universities.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tutors Grid */}
      {loading && displayTutors.length === 0 ? (
        <div className="flex justify-center items-center py-12">
          <div className="w-6 h-6 border-2 border-t-transparent border-green-500 rounded-full animate-spin"></div>
          <span className="ml-2 text-gray-600">Loading tutors...</span>
        </div>
      ) : displayTutors.length > 0 ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {displayTutors.map((tutor) => (
              <TutorCard
                key={tutor.id}
                tutor={tutor}
                onClick={() => handleTutorClick(tutor)}
              />
            ))}
          </div>

          {/* Load More Button */}
          {!isSearching && hasMore && (
            <div className="text-center">
              <button
                onClick={handleLoadMore}
                disabled={loading}
                className="px-6 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition disabled:opacity-50"
              >
                {loading ? 'Loading...' : 'Load More Tutors'}
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <GraduationCap className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-gray-800 mb-2">
            {isSearching ? 'No tutors found' : 'No tutors available'}
          </h3>
          <p className="text-gray-600 mb-4">
            {isSearching 
              ? `No tutors found for "${searchTerm}". Try a different course code.`
              : 'There are no tutors available at the moment. Please check back later.'
            }
          </p>
          {(isSearching || hasActiveFilters) && (
            <button
              onClick={() => {
                setSearchTerm('');
                clearSearch();
                resetFilters();
              }}
              className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition"
            >
              Show All Tutors
            </button>
          )}
        </div>
      )}

      {/* Tutor Modal */}
      <TutorModal
        tutor={selectedTutor}
        isOpen={showTutorModal}
        onClose={closeTutorModal}
      />
    </div>
  );
};

export default FindTutors;